import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Switch, Box,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface RoutingRule {
  id: number;
  tenant_id: number;
  name: string;
  call_type_code: string | null;
  zone: string | null;
  unit_code: string | null;
  station_id: number | null;
  agent_id_target: string | null;
  tone_key: string | null;
  priority: number;
  is_active: boolean;
  notes: string | null;
}

const EMPTY_FORM = {
  name: '', call_type_code: '', zone: '', unit_code: '',
  station_id: '', agent_id_target: '', tone_key: '', priority: '0', notes: '',
};

export default function RoutingPage() {
  const { tenantId } = useAuth();
  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/routing`);
      setRules(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = (r: RoutingRule) => {
    setEditingId(r.id);
    setForm({
      name: r.name, call_type_code: r.call_type_code || '', zone: r.zone || '',
      unit_code: r.unit_code || '', station_id: r.station_id?.toString() || '',
      agent_id_target: r.agent_id_target || '', tone_key: r.tone_key || '',
      priority: r.priority.toString(), notes: r.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tenantId) return;
    const payload: Record<string, unknown> = {
      name: form.name,
      call_type_code: form.call_type_code || null,
      zone: form.zone || null,
      unit_code: form.unit_code || null,
      station_id: form.station_id ? parseInt(form.station_id) : null,
      agent_id_target: form.agent_id_target || null,
      tone_key: form.tone_key || null,
      priority: parseInt(form.priority) || 0,
      notes: form.notes || null,
    };
    try {
      if (editingId) {
        await api.put(`/api/tenants/${tenantId}/routing/${editingId}`, payload);
      } else {
        await api.post(`/api/tenants/${tenantId}/routing`, payload);
      }
      setDialogOpen(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!tenantId || !window.confirm('Delete this routing rule?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/routing/${id}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggle = async (r: RoutingRule) => {
    if (!tenantId) return;
    try {
      await api.put(`/api/tenants/${tenantId}/routing/${r.id}`, { is_active: !r.is_active });
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Update failed');
    }
  };

  const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Routing Rules</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Rule</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Priority</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Call Type</TableCell>
              <TableCell>Zone</TableCell>
              <TableCell>Unit Code</TableCell>
              <TableCell>Tone Key</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center">Loading...</TableCell></TableRow>
            ) : rules.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No routing rules configured</TableCell></TableRow>
            ) : rules.map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.priority}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.call_type_code || '—'}</TableCell>
                <TableCell>{r.zone || '—'}</TableCell>
                <TableCell>{r.unit_code || '—'}</TableCell>
                <TableCell>{r.tone_key || '—'}</TableCell>
                <TableCell><Switch checked={r.is_active} onChange={() => handleToggle(r)} size="small" /></TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Routing Rule' : 'Add Routing Rule'}</DialogTitle>
        <DialogContent>
          <TextField label="Rule Name" fullWidth margin="dense" value={form.name} onChange={updateField('name')} required />
          <TextField label="Priority" fullWidth margin="dense" value={form.priority} onChange={updateField('priority')} type="number"
            helperText="Higher priority rules are evaluated first" />
          <TextField label="Call Type Code" fullWidth margin="dense" value={form.call_type_code} onChange={updateField('call_type_code')}
            helperText="Match alerts with this call type code (leave blank for all)" />
          <TextField label="Zone" fullWidth margin="dense" value={form.zone} onChange={updateField('zone')}
            helperText="Match alerts in this zone (leave blank for all)" />
          <TextField label="Unit Code" fullWidth margin="dense" value={form.unit_code} onChange={updateField('unit_code')}
            helperText="Match specific unit code (leave blank for all)" />
          <TextField label="Tone Key" fullWidth margin="dense" value={form.tone_key} onChange={updateField('tone_key')}
            helperText="Alert tone to play when this rule matches" />
          <TextField label="Target Agent ID" fullWidth margin="dense" value={form.agent_id_target} onChange={updateField('agent_id_target')}
            helperText="Route to specific agent (leave blank for all matching agents)" />
          <TextField label="Notes" fullWidth margin="dense" value={form.notes} onChange={updateField('notes')} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
